// src/cockpit4/components/HermesReportGenerator.tsx
// ============================================
// 信息整合与汇报组件（Hermes 助手 P1-功能5）
//
// 功能：
//   1. 自动收集各模块 Runtime 真实数据（收入 / 客户 / 内容 / 项目 / 任务）
//   2. 生成定制化报告（日报 / 周报 / 月报 / 自定义专项报告）
//   3. 数据可视化展示（报告内嵌指标条形图摘要）
//   4. 关键洞察提炼（AI 生成要点，调用 Hermes Runtime，失败降级为启发式分析）
//   5. 报告模板自定义（自定义类型支持板块增删）
//   6. 定时推送报告（配置推送时间 / 频率，到点自动生成）
//
// 数据来源（禁止假数据）：
//   - 报告记录与模板 / 定时配置：localStorage（key: xeaos_hermes_reports）持久化
//   - 各板块真实数据通过 RuntimeManager.query 自动收集：
//       · RevenueEngine → 收入摘要        RuntimeManager.query('revenue_engine', { type:'today_revenue' })
//       · CRM           → 客户动态        RuntimeManager.query('crm', { type:'stats' })
//       · Content       → 内容产出        RuntimeManager.query('content', { type:'stats' })
//       · project       → 项目进度        RuntimeManager.query('project', { type:'list' })
//       · Scheduler     → 任务进度        RuntimeManager.query('scheduler', { type:'list' })
//   - AI 摘要 / 洞察：优先调用 Hermes Runtime 生成，失败则基于真实指标启发式生成
//   - 无数据时显示"暂无报告 / 选择模板生成"
//
// 风格：深色科技玻璃拟态，紫色主色调，border-white/[0.06] + bg-white/[0.02]，framer-motion 动画
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileBarChart,
  FileText,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  Sparkles,
  RefreshCw,
  Plus,
  Trash2,
  Clock,
  Settings2,
  TrendingUp,
  TrendingDown,
  Minus,
  Lightbulb,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  FilePlus2,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义
// ============================================

/** 报告类型 */
type ReportType = "daily" | "weekly" | "monthly" | "custom";

/** 报告状态 */
type ReportStatus = "generating" | "completed" | "failed";

/** 单条指标 */
interface Metric {
  label: string;
  value: string;
  trend?: string;
}

/** 报告板块 */
interface ReportSection {
  title: string; // 板块标题（如"收入摘要"）
  metrics: Metric[];
  summary: string; // AI 生成的文字摘要
  insights?: string[]; // 关键洞察
}

/** 定时推送配置 */
interface ReportSchedule {
  enabled: boolean;
  time: string; // 推送时间 HH:mm
  frequency: "daily" | "weekly" | "monthly";
  reportType: ReportType; // 自动生成的报告类型
}

/** 报告 */
interface Report {
  id: string;
  type: ReportType;
  title: string;
  date: string; // 报告日期
  status: ReportStatus;
  sections: ReportSection[];
  createdAt: string;
  // 定时推送配置（快照）
  schedule?: {
    enabled: boolean;
    time: string; // 推送时间 HH:mm
    frequency: "daily" | "weekly" | "monthly";
  };
}

/** 自定义模板 */
interface CustomTemplate {
  title: string;
  sections: string[];
}

/** localStorage 持久化结构 */
interface ReportStore {
  reports: Report[];
  customTemplate: CustomTemplate;
  schedule: ReportSchedule;
}

// ============================================
// 常量
// ============================================

const STORAGE_KEY = "xeaos_hermes_reports";

/** 报告模板（日报 / 周报 / 月报） */
const REPORT_TEMPLATES: Record<
  Exclude<ReportType, "custom">,
  { title: string; sections: string[] }
> = {
  daily: {
    title: "日报",
    sections: ["收入摘要", "客户动态", "内容产出", "任务进度", "风险提醒"],
  },
  weekly: {
    title: "周报",
    sections: ["本周概览", "收入分析", "客户进展", "内容运营", "项目进度", "下周计划"],
  },
  monthly: {
    title: "月报",
    sections: ["月度总览", "财务分析", "客户分析", "内容分析", "项目复盘", "下月规划"],
  },
};

/** 默认自定义模板 */
const DEFAULT_CUSTOM_TEMPLATE: CustomTemplate = {
  title: "专项报告",
  sections: ["收入摘要", "客户动态", "项目进度"],
};

/** 默认定时推送配置 */
const DEFAULT_SCHEDULE: ReportSchedule = {
  enabled: false,
  time: "09:00",
  frequency: "daily",
  reportType: "daily",
};

/** 报告类型 → 图标 / 颜色 / 标签映射 */
const TYPE_META: Record<
  ReportType,
  { icon: typeof FileText; color: string; label: string }
> = {
  daily: { icon: CalendarDays, color: "#A855F7", label: "日报" },
  weekly: { icon: CalendarRange, color: "#8B5CF6", label: "周报" },
  monthly: { icon: CalendarClock, color: "#7C3AED", label: "月报" },
  custom: { icon: FilePlus2, color: "#C084FC", label: "自定义" },
};

/** 紫色主色调 */
const PRIMARY = "#A855F7";

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 读取 localStorage 全量状态 */
function loadStore(): ReportStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const initial: ReportStore = {
        reports: [],
        customTemplate: DEFAULT_CUSTOM_TEMPLATE,
        schedule: DEFAULT_SCHEDULE,
      };
      saveStore(initial);
      return initial;
    }
    const parsed = JSON.parse(raw) as Partial<ReportStore>;
    // 防御性校验
    if (!parsed || !Array.isArray(parsed.reports)) {
      const initial: ReportStore = {
        reports: [],
        customTemplate: DEFAULT_CUSTOM_TEMPLATE,
        schedule: DEFAULT_SCHEDULE,
      };
      saveStore(initial);
      return initial;
    }
    return {
      reports: parsed.reports,
      customTemplate: parsed.customTemplate ?? DEFAULT_CUSTOM_TEMPLATE,
      schedule: parsed.schedule ?? DEFAULT_SCHEDULE,
    };
  } catch {
    return {
      reports: [],
      customTemplate: DEFAULT_CUSTOM_TEMPLATE,
      schedule: DEFAULT_SCHEDULE,
    };
  }
}

/** 写入 localStorage 全量状态 */
function saveStore(store: ReportStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // 静默降级
  }
}

/** 格式化日期为 YYYY-MM-DD */
function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 格式化为相对时间描述 */
function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} 天前`;
  return new Date(iso).toLocaleDateString("zh-CN");
}

/** 判断 ISO 时间是否为今天 */
function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

// ============================================
// 安全查询封装（基于 RuntimeManager.query 真实数据）
// ============================================

/**
 * 安全查询 Runtime：未注册 / 失败 / 无数据均返回 null，避免抛错
 * @param name Runtime 名称（接受 RuntimeName 枚举或字符串，字符串用于未在枚举中登记的运行时如 'project'）
 * @param params 查询参数
 */
async function safeQuery(
  name: RuntimeName | string,
  params: Record<string, unknown>
): Promise<unknown | null> {
  try {
    // 字符串安全转为 RuntimeName：未注册时 hasRuntime 返回 false，不会触发实际查询
    const rn = name as RuntimeName;
    if (!RuntimeManager.hasRuntime(rn)) return null;
    const resp = await RuntimeManager.query(rn, params);
    if (!resp?.success) return null;
    return resp.data ?? null;
  } catch {
    return null;
  }
}

/** 从任意数据中按候选字段名提取数值 */
function pickNumber(data: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = data?.[k];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return null;
}

/** 从任意数据中按候选字段名提取字符串 */
function pickString(data: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = data?.[k];
    if (typeof v === "string" && v.trim() !== "") return v;
    if (typeof v === "number") return String(v);
  }
  return null;
}

/** 趋势格式化：将数值变化转为 "↑12.3%" / "↓5.1%" 文本 */
function formatTrend(change: number | null): string | undefined {
  if (change === null || Number.isNaN(change)) return undefined;
  if (change > 0) return `↑${change.toFixed(1)}%`;
  if (change < 0) return `↓${Math.abs(change).toFixed(1)}%`;
  return "持平";
}

// ============================================
// 数据收集器（各模块真实数据 → metrics）
// ============================================

/** 收入摘要：RuntimeManager.query('revenue_engine', { type: 'today_revenue' }) */
async function collectRevenue(): Promise<Metric[]> {
  // 按 spec 优先 today_revenue，兼容 summary
  const data =
    (await safeQuery(RuntimeName.RevenueEngine, { type: "today_revenue" })) ??
    (await safeQuery(RuntimeName.RevenueEngine, { type: "summary" }));
  if (!data) return [];
  const obj = (typeof data === "object" && data !== null ? data : {}) as Record<
    string,
    unknown
  >;

  const revenue = pickNumber(obj, [
    "todayRevenue",
    "revenue",
    "totalRevenue",
    "amount",
    "today",
  ]);
  const orders = pickNumber(obj, ["orders", "orderCount", "todayOrders", "count"]);
  const avg = pickNumber(obj, ["avgOrderValue", "aov", "average", "avg"]);
  const drop = pickNumber(obj, [
    "dailyDrop",
    "dropPercent",
    "revenueDrop",
    "change",
  ]);

  const metrics: Metric[] = [];
  if (revenue !== null)
    metrics.push({ label: "今日收入", value: `¥${revenue.toLocaleString()}` });
  if (orders !== null) metrics.push({ label: "订单数", value: String(orders) });
  if (avg !== null) metrics.push({ label: "客单价", value: `¥${avg.toFixed(2)}` });
  if (drop !== null) metrics.push({ label: "环比变化", value: `${drop.toFixed(1)}%`, trend: formatTrend(drop) });
  return metrics;
}

/** 客户动态：RuntimeManager.query('crm', { type: 'stats' }) */
async function collectCRM(): Promise<Metric[]> {
  const data = await safeQuery(RuntimeName.CRM, { type: "stats" });
  if (!data) return [];
  const obj = (typeof data === "object" && data !== null ? data : {}) as Record<
    string,
    unknown
  >;

  const total = pickNumber(obj, ["total", "totalCustomers", "customers", "count"]);
  const newCustomers = pickNumber(obj, [
    "newToday",
    "newCustomers",
    "new",
    "todayNew",
  ]);
  const pending = pickNumber(obj, ["pending", "pendingFollowups", "followups", "todo"]);
  const won = pickNumber(obj, ["won", "deals", "closedDeals", "converted"]);

  const metrics: Metric[] = [];
  if (total !== null) metrics.push({ label: "客户总数", value: String(total) });
  if (newCustomers !== null)
    metrics.push({ label: "新增客户", value: String(newCustomers) });
  if (pending !== null) metrics.push({ label: "待跟进", value: String(pending) });
  if (won !== null) metrics.push({ label: "成交数", value: String(won) });
  return metrics;
}

/** 内容产出：RuntimeManager.query('content', { type: 'stats' }) */
async function collectContent(): Promise<Metric[]> {
  const data = await safeQuery(RuntimeName.Content, { type: "stats" });
  if (!data) return [];
  const obj = (typeof data === "object" && data !== null ? data : {}) as Record<
    string,
    unknown
  >;

  const total = pickNumber(obj, ["total", "totalContent", "contents", "count"]);
  const today = pickNumber(obj, ["todayPublished", "today", "publishedToday"]);
  const published = pickNumber(obj, ["published", "publishedCount", "live"]);
  const review = pickNumber(obj, ["pendingReview", "review", "draft", "drafts"]);

  const metrics: Metric[] = [];
  if (total !== null) metrics.push({ label: "内容总数", value: String(total) });
  if (today !== null) metrics.push({ label: "今日产出", value: String(today) });
  if (published !== null) metrics.push({ label: "已发布", value: String(published) });
  if (review !== null) metrics.push({ label: "待审核", value: String(review) });
  return metrics;
}

/** 项目进度：RuntimeManager.query('project', { type: 'list' })，兼容 business / workflow */
async function collectProject(): Promise<Metric[]> {
  // 按 spec 优先 'project'，兼容 Business / Workflow
  const data =
    (await safeQuery("project", { type: "list" })) ??
    (await safeQuery(RuntimeName.Business, { type: "project_list" })) ??
    (await safeQuery(RuntimeName.Business, { type: "list" })) ??
    (await safeQuery(RuntimeName.Workflow, { type: "list" }));
  if (!data) return [];

  // 兼容数组与对象结构
  let list: unknown[] = [];
  if (Array.isArray(data)) list = data;
  else if (typeof data === "object" && data !== null) {
    const obj = data as Record<string, unknown>;
    list = Array.isArray(obj.items) ? obj.items : Array.isArray(obj.list) ? obj.list : [];
    // 若对象本身含汇总字段，优先用汇总
    const total = pickNumber(obj, ["total", "totalProjects", "count"]);
    if (total !== null) {
      const active = pickNumber(obj, ["active", "inProgress", "ongoing"]);
      const done = pickNumber(obj, ["done", "completed", "finished"]);
      const overdue = pickNumber(obj, ["overdue", "delayed", "late"]);
      const metrics: Metric[] = [{ label: "项目总数", value: String(total) }];
      if (active !== null) metrics.push({ label: "进行中", value: String(active) });
      if (done !== null) metrics.push({ label: "已完成", value: String(done) });
      if (overdue !== null) metrics.push({ label: "逾期", value: String(overdue) });
      return metrics;
    }
  }

  if (list.length === 0) return [];
  // 从列表项统计状态
  let active = 0;
  let done = 0;
  let overdue = 0;
  for (const item of list) {
    const it = (typeof item === "object" && item !== null ? item : {}) as Record<
      string,
      unknown
    >;
    const status = pickString(it, ["status", "state"])?.toLowerCase() ?? "";
    if (status.includes("done") || status.includes("complete") || status.includes("finish"))
      done++;
    else if (status.includes("overdue") || status.includes("delay") || status.includes("late"))
      overdue++;
    else if (status.includes("progress") || status.includes("active") || status.includes("ongoing"))
      active++;
  }
  return [
    { label: "项目总数", value: String(list.length) },
    { label: "进行中", value: String(active) },
    { label: "已完成", value: String(done) },
    { label: "逾期", value: String(overdue) },
  ];
}

/** 任务进度：RuntimeManager.query('scheduler', { type: 'list' }) */
async function collectTasks(): Promise<Metric[]> {
  const data = await safeQuery(RuntimeName.Scheduler, { type: "list" });
  if (!data) return [];

  let list: unknown[] = [];
  if (Array.isArray(data)) list = data;
  else if (typeof data === "object" && data !== null) {
    const obj = data as Record<string, unknown>;
    list = Array.isArray(obj.items) ? obj.items : Array.isArray(obj.list) ? obj.list : Array.isArray(obj.tasks) ? obj.tasks : [];
    const total = pickNumber(obj, ["total", "taskCount", "count"]);
    if (total !== null) {
      const done = pickNumber(obj, ["done", "completed", "finished"]);
      const active = pickNumber(obj, ["active", "inProgress", "ongoing"]);
      const overdue = pickNumber(obj, ["overdue", "delayed", "late"]);
      const metrics: Metric[] = [{ label: "任务总数", value: String(total) }];
      if (done !== null) metrics.push({ label: "已完成", value: String(done) });
      if (active !== null) metrics.push({ label: "进行中", value: String(active) });
      if (overdue !== null) metrics.push({ label: "逾期", value: String(overdue) });
      return metrics;
    }
  }

  if (list.length === 0) return [];
  let done = 0;
  let active = 0;
  let overdue = 0;
  for (const item of list) {
    const it = (typeof item === "object" && item !== null ? item : {}) as Record<
      string,
      unknown
    >;
    const status = pickString(it, ["status", "state"])?.toLowerCase() ?? "";
    if (status.includes("done") || status.includes("complete") || status.includes("finish"))
      done++;
    else if (status.includes("overdue") || status.includes("delay") || status.includes("late"))
      overdue++;
    else if (status.includes("progress") || status.includes("active") || status.includes("ongoing"))
      active++;
  }
  return [
    { label: "任务总数", value: String(list.length) },
    { label: "进行中", value: String(active) },
    { label: "已完成", value: String(done) },
    { label: "逾期", value: String(overdue) },
  ];
}

// ============================================
// AI 摘要与洞察生成（优先 Hermes Runtime，降级启发式）
// ============================================

/** 从 Hermes 响应中提取文本 */
function extractHermesText(data: unknown): string | null {
  if (!data) return null;
  if (typeof data === "string") return data;
  if (typeof data === "object") {
    const obj = data as Record<string, unknown>;
    const candidates = ["content", "text", "message", "result", "summary", "response"];
    for (const k of candidates) {
      const v = obj[k];
      if (typeof v === "string" && v.trim() !== "") return v;
    }
    // 嵌套 data 字段
    if (typeof obj.data === "object" && obj.data !== null) {
      return extractHermesText(obj.data);
    }
  }
  return null;
}

/** 启发式摘要：基于真实指标生成文字（无 LLM 时的降级，仍基于真实数据） */
function heuristicSummary(title: string, metrics: Metric[]): string {
  if (metrics.length === 0) {
    return `${title}暂无可用数据，相关模块 Runtime 未就绪或未返回数据。`;
  }
  const parts: string[] = [];
  for (const m of metrics) {
    let line = `${m.label} ${m.value}`;
    if (m.trend) line += `（${m.trend}）`;
    parts.push(line);
  }
  // 检测异常趋势
  const downTrend = metrics.find(
    (m) => m.trend && m.trend.startsWith("↓")
  );
  let tail = "";
  if (downTrend) {
    tail = `其中${downTrend.label}出现下滑，建议关注。`;
  }
  return `${title}：${parts.join("，")}。${tail}`.trim();
}

/** 启发式洞察：从指标中提炼要点 */
function heuristicInsights(title: string, metrics: Metric[]): string[] {
  const insights: string[] = [];
  if (metrics.length === 0) return insights;

  // 1. 下跌指标 → 风险提示
  for (const m of metrics) {
    if (m.trend && m.trend.startsWith("↓")) {
      insights.push(`${m.label}环比${m.trend}，存在下行风险，需重点排查原因。`);
    }
  }
  // 2. 逾期类指标 → 告警
  const overdue = metrics.find((m) => m.label.includes("逾期"));
  if (overdue && Number(overdue.value) > 0) {
    insights.push(`当前有 ${overdue.value} 项逾期，建议优先处理以降低风险。`);
  }
  // 3. 待处理类指标 → 跟进建议
  const pending = metrics.find(
    (m) => m.label.includes("待") || m.label.includes("跟进") || m.label.includes("审核")
  );
  if (pending && Number(pending.value) > 0) {
    insights.push(`${pending.label} ${pending.value} 项待处理，建议今日内推进。`);
  }
  // 4. 无异常时给出正向洞察
  if (insights.length === 0) {
    insights.push(`${title}各项指标平稳运行，建议保持当前节奏。`);
  }
  return insights;
}

/**
 * 生成板块 AI 摘要
 * 优先调用 Hermes Runtime 生成，失败降级为基于真实指标的启发式摘要
 */
async function generateSectionSummary(
  title: string,
  metrics: Metric[]
): Promise<string> {
  // 构造上下文（仅真实数据）
  const context = metrics.length
    ? metrics.map((m) => `- ${m.label}: ${m.value}${m.trend ? ` (${m.trend})` : ""}`).join("\n")
    : "（暂无数据）";

  // 尝试调用 Hermes Runtime 生成 AI 摘要
  try {
    if (RuntimeManager.hasRuntime(RuntimeName.Hermes)) {
      const prompt =
        `你是企业助手 Hermes。请基于以下"${title}"板块的真实数据，生成一段简短的中文摘要（50-100字），` +
        `客观陈述现状并指出需要注意的点。数据如下：\n${context}`;
      const resp = await RuntimeManager.execute(RuntimeName.Hermes, "generate", {
        content: prompt,
      });
      const text = resp?.success ? extractHermesText(resp.data) : null;
      if (text && text.trim().length > 0) return text.trim();
    }
  } catch {
    // 降级
  }
  // 降级：基于真实指标的启发式摘要
  return heuristicSummary(title, metrics);
}

/**
 * 生成板块关键洞察
 * 优先调用 Hermes Runtime 生成，失败降级为启发式提炼
 */
async function generateSectionInsights(
  title: string,
  metrics: Metric[]
): Promise<string[]> {
  if (metrics.length === 0) return [];

  // 尝试调用 Hermes Runtime 生成洞察
  try {
    if (RuntimeManager.hasRuntime(RuntimeName.Hermes)) {
      const context = metrics
        .map((m) => `- ${m.label}: ${m.value}${m.trend ? ` (${m.trend})` : ""}`)
        .join("\n");
      const prompt =
        `你是企业助手 Hermes。基于以下"${title}"板块真实数据，提炼 2-3 条关键洞察（每条一句话，聚焦风险与机会）。\n` +
        `数据：\n${context}\n请用 JSON 数组格式返回，如 ["洞察1","洞察2"]。`;
      const resp = await RuntimeManager.execute(RuntimeName.Hermes, "generate", {
        content: prompt,
      });
      const text = resp?.success ? extractHermesText(resp.data) : null;
      if (text) {
        // 尝试解析 JSON 数组
        const match = text.match(/\[[\s\S]*\]/);
        if (match) {
          try {
            const arr = JSON.parse(match[0]) as unknown;
            if (Array.isArray(arr) && arr.length > 0) {
              const insights = arr
                .filter((x): x is string => typeof x === "string" && x.trim() !== "")
                .map((x) => x.trim());
              if (insights.length > 0) return insights.slice(0, 3);
            }
          } catch {
            // 解析失败，降级
          }
        }
      }
    }
  } catch {
    // 降级
  }
  // 降级：启发式提炼
  return heuristicInsights(title, metrics);
}

// ============================================
// 板块标题 → 数据收集器映射
// ============================================

/** 根据板块标题选择数据收集器 */
function pickCollector(title: string): (() => Promise<Metric[]>) | null {
  const t = title.toLowerCase();
  if (t.includes("收入") || t.includes("财务") || t.includes("概览") || t.includes("总览"))
    return collectRevenue;
  if (t.includes("客户")) return collectCRM;
  if (t.includes("内容")) return collectContent;
  if (t.includes("项目")) return collectProject;
  if (t.includes("任务") || t.includes("计划")) return collectTasks;
  if (t.includes("风险")) return collectTasks; // 风险提醒基于任务逾期
  return null;
}

/** 生成单个板块 */
async function buildSection(title: string): Promise<ReportSection> {
  const collector = pickCollector(title);
  const metrics = collector ? await collector() : [];

  // 风险提醒板块：聚合各模块逾期指标
  let finalMetrics = metrics;
  if (title.includes("风险")) {
    const [rev, crm, content, proj, task] = await Promise.all([
      collectRevenue(),
      collectCRM(),
      collectContent(),
      collectProject(),
      collectTasks(),
    ]);
    finalMetrics = [];
    const all = [...rev, ...crm, ...content, ...proj, ...task];
    // 仅保留含下跌趋势或逾期类指标
    const risks = all.filter(
      (m) =>
        (m.trend && m.trend.startsWith("↓")) ||
        m.label.includes("逾期") ||
        m.label.includes("待")
    );
    finalMetrics = risks.length > 0 ? risks : all.slice(0, 3);
  }

  const [summary, insights] = await Promise.all([
    generateSectionSummary(title, finalMetrics),
    generateSectionInsights(title, finalMetrics),
  ]);

  return {
    title,
    metrics: finalMetrics,
    summary,
    insights,
  };
}

// ============================================
// 主组件
// ============================================

export default function HermesReportGenerator() {
  const [store, setStore] = useState<ReportStore>(() => loadStore());
  const [selectedType, setSelectedType] = useState<ReportType>("daily");
  const [generating, setGenerating] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // 模板编辑态
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");

  // 定时推送配置展开态
  const [expandedSchedule, setExpandedSchedule] = useState(false);

  // 自动推送节流：避免同分钟内重复触发
  const lastAutoGenRef = useRef<string>("");

  // ============================================
  // 持久化：每次 store 变更写回 localStorage
  // ============================================
  useEffect(() => {
    saveStore(store);
  }, [store]);

  // ============================================
  // Toast 自动消失
  // ============================================
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ============================================
  // 已生成报告列表：按日期倒序
  // ============================================
  const sortedReports = useMemo(() => {
    return [...store.reports].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [store.reports]);

  // ============================================
  // 当前选中的报告
  // ============================================
  const selectedReport = useMemo(() => {
    if (!selectedReportId) return sortedReports[0] ?? null;
    return store.reports.find((r) => r.id === selectedReportId) ?? null;
  }, [selectedReportId, sortedReports, store.reports]);

  // ============================================
  // 获取当前类型的板块列表
  // ============================================
  const getCurrentSections = useCallback((type: ReportType): string[] => {
    if (type === "custom") return store.customTemplate.sections;
    return REPORT_TEMPLATES[type].sections;
  }, [store.customTemplate.sections]);

  // ============================================
  // 操作：生成报告
  // ============================================
  const handleGenerate = useCallback(
    async (type: ReportType) => {
      setGenerating(true);
      setToast(null);
      try {
        const sections = getCurrentSections(type);
        const title =
          type === "custom"
            ? store.customTemplate.title
            : REPORT_TEMPLATES[type].title;
        const dateStr = formatDate(new Date());

        // 创建一个 generating 状态的报告占位
        const reportId = genId();
        const placeholder: Report = {
          id: reportId,
          type,
          title: `${title} · ${dateStr}`,
          date: dateStr,
          status: "generating",
          sections: [],
          createdAt: new Date().toISOString(),
        };
        setStore((prev) => ({
          ...prev,
          reports: [placeholder, ...prev.reports],
        }));
        setSelectedReportId(reportId);

        // 并行收集所有板块
        const builtSections = await Promise.all(
          sections.map((s) => buildSection(s))
        );

        const completed: Report = {
          ...placeholder,
          status: "completed",
          sections: builtSections,
        };

        setStore((prev) => ({
          ...prev,
          reports: prev.reports.map((r) =>
            r.id === reportId ? completed : r
          ),
        }));
        setToast(`${title}已生成（${builtSections.length} 个板块）`);
      } catch (err) {
        // 标记失败
        setStore((prev) => ({
          ...prev,
          reports: prev.reports.map((r) =>
            r.status === "generating"
              ? { ...r, status: "failed" as ReportStatus }
              : r
          ),
        }));
        setToast(`报告生成失败：${(err as Error).message}`);
      } finally {
        setGenerating(false);
      }
    },
    [getCurrentSections, store.customTemplate.title]
  );

  // ============================================
  // 操作：删除报告
  // ============================================
  const handleDeleteReport = useCallback((id: string) => {
    setStore((prev) => ({
      ...prev,
      reports: prev.reports.filter((r) => r.id !== id),
    }));
    setSelectedReportId((cur) => (cur === id ? null : cur));
    setToast("报告已删除");
  }, []);

  // ============================================
  // 操作：定时推送配置更新
  // ============================================
  const updateSchedule = useCallback(
    (patch: Partial<ReportSchedule>) => {
      setStore((prev) => ({
        ...prev,
        schedule: { ...prev.schedule, ...patch },
      }));
    },
    []
  );

  // ============================================
  // 操作：自定义模板 - 添加板块
  // ============================================
  const addCustomSection = useCallback(() => {
    const name = newSectionName.trim();
    if (!name) return;
    setStore((prev) => ({
      ...prev,
      customTemplate: {
        ...prev.customTemplate,
        sections: [...prev.customTemplate.sections, name],
      },
    }));
    setNewSectionName("");
  }, [newSectionName]);

  // ============================================
  // 操作：自定义模板 - 删除板块
  // ============================================
  const removeCustomSection = useCallback((idx: number) => {
    setStore((prev) => ({
      ...prev,
      customTemplate: {
        ...prev.customTemplate,
        sections: prev.customTemplate.sections.filter((_, i) => i !== idx),
      },
    }));
  }, []);

  // ============================================
  // 操作：更新自定义模板标题
  // ============================================
  const updateCustomTitle = useCallback((title: string) => {
    setStore((prev) => ({
      ...prev,
      customTemplate: { ...prev.customTemplate, title },
    }));
  }, []);

  // ============================================
  // 定时推送：到点自动生成报告
  // 每分钟检查一次，匹配配置时间且当日未生成则触发
  // ============================================
  useEffect(() => {
    if (!store.schedule.enabled) return;
    const check = () => {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const nowTime = `${hh}:${mm}`;
      // 频率匹配
      const freqMatch =
        store.schedule.frequency === "daily" ||
        (store.schedule.frequency === "weekly" && now.getDay() === 1) ||
        (store.schedule.frequency === "monthly" && now.getDate() === 1);
      if (!freqMatch) return;
      if (nowTime !== store.schedule.time) return;

      // 节流：同一分钟内只触发一次
      const key = `${nowTime}_${now.toDateString()}`;
      if (lastAutoGenRef.current === key) return;
      lastAutoGenRef.current = key;

      // 当天是否已生成该类型报告
      const todayExists = store.reports.some(
        (r) => r.type === store.schedule.reportType && isToday(r.createdAt)
      );
      if (todayExists) return;
      // 触发自动生成
      handleGenerate(store.schedule.reportType);
    };
    const timer = setInterval(check, 60 * 1000);
    // 挂载时立即检查一次
    check();
    return () => clearInterval(timer);
  }, [store.schedule, store.reports, handleGenerate]);

  // ============================================
  // 渲染：状态标签
  // ============================================
  const renderStatusBadge = (status: ReportStatus) => {
    if (status === "generating")
      return (
        <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-amber-500/15 text-amber-400">
          <RefreshCw size={8} className="animate-spin" />
          生成中
        </span>
      );
    if (status === "failed")
      return (
        <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-red-500/15 text-red-400">
          <AlertTriangle size={8} />
          失败
        </span>
      );
    return (
      <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-emerald-500/15 text-emerald-400">
        <CheckCircle2 size={8} />
        完成
      </span>
    );
  };

  // ============================================
  // 渲染：趋势图标
  // ============================================
  const renderTrend = (trend?: string) => {
    if (!trend) return null;
    if (trend.startsWith("↑"))
      return (
        <span className="flex items-center gap-0.5 text-emerald-400">
          <TrendingUp size={9} />
          {trend}
        </span>
      );
    if (trend.startsWith("↓"))
      return (
        <span className="flex items-center gap-0.5 text-red-400">
          <TrendingDown size={9} />
          {trend}
        </span>
      );
    return (
      <span className="flex items-center gap-0.5 text-gray-400">
        <Minus size={9} />
        {trend}
      </span>
    );
  };

  // ============================================
  // 渲染：板块内嵌图表摘要（指标条形图）
  // ============================================
  const renderMetricsChart = (metrics: Metric[]) => {
    if (metrics.length === 0) return null;
    // 提取数值用于条形长度计算
    const numericValues = metrics.map((m) => {
      const num = parseFloat(m.value.replace(/[^0-9.\-]/g, ""));
      return Number.isNaN(num) ? 0 : Math.max(0, num);
    });
    const max = Math.max(...numericValues, 1);
    return (
      <div className="mt-2 space-y-1">
        {metrics.map((m, i) => {
          const val = numericValues[i];
          const ratio = max > 0 ? Math.min(1, val / max) : 0;
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[9px] font-mono text-gray-400 w-16 truncate flex-shrink-0">
                {m.label}
              </span>
              <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.max(4, ratio * 100)}%` }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="h-full rounded-full bg-gradient-to-r from-purple-500/60 to-purple-400/80"
                />
              </div>
              <span className="text-[9px] font-mono text-white w-14 text-right truncate">
                {m.value}
              </span>
              <span className="w-12 flex-shrink-0">{renderTrend(m.trend)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带（紫色主色调） */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <FileBarChart size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              信息整合与汇报
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Hermes Report Generator
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 定时推送配置 */}
          <button
            onClick={() => setExpandedSchedule((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${
              store.schedule.enabled
                ? "bg-purple-500/15 border-purple-500/30 hover:bg-purple-500/25"
                : "bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.05]"
            }`}
            title="定时推送报告配置"
          >
            <Clock
              size={11}
              className={store.schedule.enabled ? "text-purple-400" : "text-gray-500"}
            />
            <span
              className={`text-[9px] font-mono uppercase tracking-wider ${
                store.schedule.enabled ? "text-purple-300" : "text-gray-400"
              }`}
            >
              {store.schedule.enabled ? "推送" : "推送"}
            </span>
            {store.schedule.enabled && (
              <span className="text-[8px] font-mono text-purple-300/80">
                {store.schedule.time}
              </span>
            )}
          </button>

          {/* 生成按钮 */}
          <button
            onClick={() => handleGenerate(selectedType)}
            disabled={generating}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/20 border border-purple-500/40 hover:bg-purple-500/30 transition-colors disabled:opacity-50"
            title="生成报告（自动收集各模块 Runtime 真实数据）"
          >
            {generating ? (
              <RefreshCw size={11} className="text-purple-300 animate-spin" />
            ) : (
              <Sparkles size={11} className="text-purple-300" />
            )}
            <span className="text-[9px] font-mono text-purple-200 uppercase tracking-wider">
              {generating ? "生成中..." : "生成"}
            </span>
          </button>
        </div>
      </div>

      {/* 顶部：报告类型选择 */}
      <div className="px-4 py-3 border-b border-white/[0.04]">
        <div className="flex items-center gap-1.5 flex-wrap">
          {(["daily", "weekly", "monthly", "custom"] as ReportType[]).map((t) => {
            const meta = TYPE_META[t];
            const Icon = meta.icon;
            const active = selectedType === t;
            return (
              <button
                key={t}
                onClick={() => setSelectedType(t)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-all ${
                  active
                    ? "bg-purple-500/15 border-purple-500/40 text-purple-200"
                    : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:bg-white/[0.04]"
                }`}
              >
                <Icon size={11} style={{ color: active ? PRIMARY : undefined }} />
                <span className="text-[10px] font-mono">{meta.label}</span>
              </button>
            );
          })}

          {/* 自定义模板编辑入口 */}
          {selectedType === "custom" && (
            <button
              onClick={() => setEditingTemplate((v) => !v)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors ml-auto"
              title="编辑自定义报告模板"
            >
              <Settings2 size={11} className="text-purple-400" />
              <span className="text-[9px] font-mono text-gray-400 uppercase">
                模板
              </span>
            </button>
          )}
        </div>

        {/* 当前模板板块预览 */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className="text-[8px] font-mono text-gray-600 uppercase">
            板块:
          </span>
          {getCurrentSections(selectedType).map((s, i) => (
            <span
              key={i}
              className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-purple-500/10 text-purple-300/90 border border-purple-500/20"
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* 定时推送配置面板 */}
      <AnimatePresence>
        {expandedSchedule && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-white/[0.04]"
          >
            <div className="px-4 py-3">
              <div className="flex items-center gap-3 flex-wrap">
                {/* 启用开关 */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-mono text-gray-400 uppercase">
                    定时推送
                  </span>
                  <button
                    onClick={() =>
                      updateSchedule({ enabled: !store.schedule.enabled })
                    }
                    className={`relative w-8 h-4 rounded-full transition-colors ${
                      store.schedule.enabled
                        ? "bg-purple-500/50"
                        : "bg-white/[0.08]"
                    }`}
                    title={store.schedule.enabled ? "关闭定时推送" : "开启定时推送"}
                  >
                    <span
                      className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                        store.schedule.enabled ? "left-4" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>

                {/* 报告类型 */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-gray-500">
                    类型
                  </span>
                  <select
                    value={store.schedule.reportType}
                    onChange={(e) =>
                      updateSchedule({
                        reportType: e.target.value as ReportType,
                      })
                    }
                    className="px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
                  >
                    <option value="daily">日报</option>
                    <option value="weekly">周报</option>
                    <option value="monthly">月报</option>
                  </select>
                </div>

                {/* 频率 */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-gray-500">
                    频率
                  </span>
                  <select
                    value={store.schedule.frequency}
                    onChange={(e) =>
                      updateSchedule({
                        frequency: e.target
                          .value as ReportSchedule["frequency"],
                      })
                    }
                    className="px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
                  >
                    <option value="daily">每日</option>
                    <option value="weekly">每周一</option>
                    <option value="monthly">每月1日</option>
                  </select>
                </div>

                {/* 时间 */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-gray-500">
                    时间
                  </span>
                  <input
                    type="time"
                    value={store.schedule.time}
                    onChange={(e) => updateSchedule({ time: e.target.value })}
                    className="px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
                  />
                </div>

                <span className="text-[8px] font-mono text-gray-600 ml-auto">
                  到点自动生成并记录至历史
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 自定义模板编辑面板 */}
      <AnimatePresence>
        {editingTemplate && selectedType === "custom" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-white/[0.04]"
          >
            <div className="px-4 py-3 space-y-2">
              {/* 模板标题 */}
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-mono text-gray-500 uppercase w-12">
                  标题
                </span>
                <input
                  type="text"
                  value={store.customTemplate.title}
                  onChange={(e) => updateCustomTitle(e.target.value)}
                  className="flex-1 px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
                  placeholder="专项报告标题"
                />
              </div>

              {/* 板块列表 */}
              <div className="space-y-1">
                {store.customTemplate.sections.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-2 py-1 rounded bg-white/[0.02] border border-white/[0.06]"
                  >
                    <span className="text-[9px] font-mono text-gray-600 w-5">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 text-[10px] font-mono text-gray-200">
                      {s}
                    </span>
                    <button
                      onClick={() => removeCustomSection(i)}
                      className="p-0.5 rounded text-gray-500 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      title="删除板块"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                ))}
              </div>

              {/* 添加板块 */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addCustomSection();
                  }}
                  className="flex-1 px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
                  placeholder="新增板块名（如：收入摘要 / 客户动态 / 项目进度）"
                />
                <button
                  onClick={addCustomSection}
                  disabled={!newSectionName.trim()}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-40"
                  title="添加板块"
                >
                  <Plus size={11} className="text-purple-300" />
                  <span className="text-[9px] font-mono text-purple-300 uppercase">
                    添加
                  </span>
                </button>
              </div>
              <p className="text-[8px] font-mono text-gray-600">
                提示：板块名含"收入/财务"查 RevenueEngine，含"客户"查 CRM，含"内容"查 Content，含"项目"查 Project，含"任务/计划"查 Scheduler。
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast 提示 */}
      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-white/[0.04]"
          >
            <div className="px-4 py-2">
              <div className="px-3 py-2 rounded-md border border-purple-500/30 bg-purple-500/[0.06] text-[10px] font-mono text-purple-300">
                {toast}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 中部：已生成报告列表 */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            报告历史
          </span>
          <span className="text-[9px] font-mono text-gray-600">
            共 {store.reports.length} 份
          </span>
        </div>

        {sortedReports.length === 0 ? (
          <div className="py-10 text-center">
            <FileText size={28} className="mx-auto text-gray-700 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">暂无报告</p>
            <p className="text-[9px] font-mono text-gray-600 mt-1">
              选择模板生成 / 点击「生成」自动收集各模块数据
            </p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
            {sortedReports.map((r) => {
              const meta = TYPE_META[r.type];
              const Icon = meta.icon;
              const active = selectedReport?.id === r.id;
              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  onClick={() => setSelectedReportId(r.id)}
                  className={`rounded-md border p-2.5 cursor-pointer transition-colors ${
                    active
                      ? "border-purple-500/40 bg-purple-500/[0.06]"
                      : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center"
                      style={{ background: `${meta.color}15` }}
                    >
                      <Icon size={11} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] font-semibold text-white truncate">
                          {r.title}
                        </span>
                        {renderStatusBadge(r.status)}
                      </div>
                      <p className="text-[8px] font-mono text-gray-600 mt-0.5">
                        {r.date} · {formatRelativeTime(r.createdAt)} · {r.sections.length} 板块
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteReport(r.id);
                      }}
                      className="p-1 rounded text-gray-500 hover:text-red-300 hover:bg-red-500/10 transition-colors flex-shrink-0"
                      title="删除报告"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* 底部：选中报告详情 */}
      <AnimatePresence mode="wait">
        {selectedReport ? (
          <motion.div
            key={selectedReport.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="border-t border-white/[0.04]"
          >
            {/* 报告头 */}
            <div className="px-4 py-3 border-b border-white/[0.04] bg-white/[0.01]">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <BarChart3 size={12} className="text-purple-400 flex-shrink-0" />
                  <h4 className="text-[12px] font-semibold text-white truncate">
                    {selectedReport.title}
                  </h4>
                  {renderStatusBadge(selectedReport.status)}
                </div>
                <span className="text-[9px] font-mono text-gray-600 flex-shrink-0">
                  {selectedReport.date}
                </span>
              </div>
            </div>

            {/* 板块内容 */}
            <div className="p-4 space-y-3 max-h-[520px] overflow-y-auto">
              {selectedReport.sections.length === 0 ? (
                <div className="py-8 text-center">
                  <RefreshCw
                    size={20}
                    className={`mx-auto mb-2 ${
                      selectedReport.status === "generating"
                        ? "text-purple-400 animate-spin"
                        : "text-gray-700"
                    }`}
                  />
                  <p className="text-[10px] font-mono text-gray-500">
                    {selectedReport.status === "generating"
                      ? "正在收集各模块数据并生成报告..."
                      : selectedReport.status === "failed"
                      ? "报告生成失败，请重试"
                      : "暂无板块数据"}
                  </p>
                </div>
              ) : (
                selectedReport.sections.map((sec, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3"
                  >
                    {/* 板块标题 */}
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="w-1 h-3 rounded-full"
                        style={{ background: PRIMARY }}
                      />
                      <span className="text-[11px] font-semibold text-white">
                        {sec.title}
                      </span>
                      <span className="text-[8px] font-mono text-gray-600 ml-auto">
                        {sec.metrics.length} 指标
                      </span>
                    </div>

                    {/* 指标 + 内嵌图表摘要 */}
                    {sec.metrics.length > 0 ? (
                      renderMetricsChart(sec.metrics)
                    ) : (
                      <p className="text-[9px] font-mono text-gray-600 py-2 text-center">
                        该板块暂无数据（对应 Runtime 未就绪或未返回数据）
                      </p>
                    )}

                    {/* AI 文字摘要 */}
                    <div className="mt-2 flex items-start gap-1.5 px-2 py-1.5 rounded bg-purple-500/[0.04] border border-purple-500/15">
                      <Sparkles
                        size={10}
                        className="text-purple-400 mt-0.5 flex-shrink-0"
                      />
                      <p className="text-[10px] text-purple-200/90 leading-snug">
                        {sec.summary}
                      </p>
                    </div>

                    {/* 关键洞察 */}
                    {sec.insights && sec.insights.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {sec.insights.map((ins, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-1.5 px-2 py-1 rounded bg-amber-500/[0.04] border border-amber-500/15"
                          >
                            <Lightbulb
                              size={10}
                              className="text-amber-400 mt-0.5 flex-shrink-0"
                            />
                            <p className="text-[9px] font-mono text-amber-200/90 leading-snug">
                              {ins}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}
